import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataPage, DataQuery } from '../../../models/data-source';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { StageCatalogEntry } from '../../../shared/stage-catalog';
import { RollOffHireDetailComponent } from './rolloff-hire-detail.component';
import { RollOffHireDialogComponent, RollOffHireDialogData } from './rolloff-hire-dialog.component';
import { DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import {
  DataFilterField, DataFilterValues,
} from '../../../shared/ui/data-filter/data-filter.component';
import { toRuleParams } from '../../../shared/ui/data-filter/data-filter.rules';
import { AuthService } from '../../../services/auth.service';
import type { AssignableRep } from '../board/leads-board.component';
import { rollOffRuleFilterFields } from './rolloff-filters';
import {
  ROLLOFF_LIST_COLUMNS, rollOffContainerLabel, rollOffHireOverdue,
} from './rolloff-list-table';
import {
  ROLLOFF_CONTAINER_TYPES_URL, ROLLOFF_MAX_PAGE_SIZE, ROLLOFF_OPPORTUNITIES_URL,
  ROLLOFF_TRANSFER_EXPORT_URL,
  hireAddress, RollOffContainerType, RollOffOpportunity, RollOffOpportunityPage,
} from './rolloff-opportunity.model';

/**
 * Roll-off hires as a TABLE, beside the board that was the only way to see
 * them.
 *
 * The board answers "what is where in the pipeline". It cannot answer "which
 * hires end this week", "what did we quote", or "sort by price" - a card has
 * room for a site and a stage and the rest of a hire is not on it. Leads
 * carried both presentations from the start; this is the missing half for
 * roll-off, built out of the same data table rather than a second one.
 *
 * WHAT IS REUSED, and it is nearly all of it: the table component owns the
 * headers, sorting, the column picker, the search box, paging and the stored
 * per-user column choice, and the filter bar is the board's, built from the
 * same column list. What this file adds is the list of columns, the fetch, and
 * the cells - which is the part that genuinely differs between a hire and a
 * lead.
 */
@Component({
  selector: 'app-rolloff-list',
  templateUrl: './rolloff-list.component.html',
  styleUrls: ['./rolloff-list.component.css'],
  host: { class: 'workspace-page workspace-page--full' },
})
export class RollOffListComponent implements OnInit, OnChanges, OnDestroy {
  /** The roll-off stages, read once by the workspace for every view. The hire
   *  detail is given them, and the Stage filter below lists them. */
  @Input() stages: StageCatalogEntry[] = [];
  private readonly destroyed$ = new Subject<void>();
  @ViewChild('compactList') compactList?: DataListComponent<RollOffOpportunity>;
  collectionSearch = '';

  readonly columns = ROLLOFF_LIST_COLUMNS;

  /**
   * The roster, so the table names a rep rather than printing a user id.
   *
   * The lead pipeline's roster, which is the board's too - it is the same
   * reps, and a second endpoint answering the same question is a second thing
   * to keep in step. A failure leaves the id showing, exactly as the board
   * does: a list that will not load because the roster did not is worse.
   */
  reps: AssignableRep[] = [];

  // ── The filter bar ─────────────────────────────────────────────────────
  //
  // The board's bar, from the same column list (rolloff-filters.ts), so a hire
  // is narrowed by the same columns whichever view is open. Applying hands the
  // values to the table, which reads its page again from the first.

  private containerTypes: RollOffContainerType[] = [];

  /** Every column from the start, so a rule applied before the dropdowns'
   *  options land still reaches the server; rebuilt as each list arrives. */
  filterFields: DataFilterField[] = rollOffRuleFilterFields([], [], []);
  filterValues: DataFilterValues = {};

  /** Opening a hire is the workspace's business: it keeps the collection
   *  mounted and routes the detail beside it. */
  @Output() open = new EventEmitter<number>();

  @ViewChild('table') table?: DataTableComponent<RollOffOpportunity>;

  constructor(
    private http: HttpClient,
    public auth: AuthService,
    private dialog: MatDialog,
  ) {}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  editCompactHire(row: RollOffOpportunity, detail: RollOffHireDetailComponent): void {
    if (!this.auth.hasPermission('LeadPipeline.Manage')) return;
    const data: RollOffHireDialogData = { hire: row, reps: this.reps };
    this.dialog.open(RollOffHireDialogComponent, { width: '560px', data })
      .afterClosed().pipe(takeUntil(this.destroyed$)).subscribe((saved?: RollOffOpportunity) => {
        if (!saved) return;
        const item = this.compactList?.items.find(item => item.id === saved.id);
        if (item) Object.assign(item, saved);
        if (item && this.compactList?.expandedItem === item) detail.load();
      });
  }

  /** The workspace hands the stages over when its context lands, which is
   *  usually after this list has started, and again whenever that context
   *  changes. The Stage rule lists whichever it holds now. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stages']) this.buildFilterFields();
  }

  ngOnInit(): void {
    this.http.get<AssignableRep[]>('/api/sales-routes/reps').subscribe({
      next: rows => { this.reps = rows ?? []; this.buildFilterFields(); },
      error: () => { /* no roster: the column names owners by id */ },
    });

    // The Container rule lists the tenant's own sizes. A failed read leaves
    // that dropdown empty, which offers nothing rather than offering nonsense
    // - and the table loads regardless. The stages are not read here: the
    // workspace already holds them and passes them in.
    this.http.get<RollOffContainerType[]>(ROLLOFF_CONTAINER_TYPES_URL).subscribe({
      next: sizes => { this.containerTypes = sizes ?? []; this.buildFilterFields(); },
      error: () => { /* the Container filter simply has nothing to offer */ },
    });
  }

  /** Rebuilt as each list lands and whenever the stages change, rather than
   *  computed in a getter, for the reason the board gives: a fresh array every
   *  change-detection pass would re-enter the filter bar's ngOnChanges for
   *  nothing. */
  private buildFilterFields(): void {
    this.filterFields = rollOffRuleFilterFields(this.containerTypes, this.stages, this.reps);
  }

  /** The bar applied its values. A new object is the table's signal to read
   *  the first page again under them. */
  applyFilters(values: DataFilterValues): void {
    this.filterValues = values;
  }

  /** A rep's name, or the id the row carries when the roster is unreadable.
   *  Spelled the same way the board spells it, so one hire does not read as
   *  two different people depending on which view is open. */
  repName(hire: RollOffOpportunity): string {
    if (hire.salesRepUserId == null) return 'Unassigned';
    const rep = this.reps.find(candidate => candidate.userId === hire.salesRepUserId);
    return rep?.displayName?.trim() || rep?.userName?.trim() || `Rep ${hire.salesRepUserId}`;
  }

  /**
   * Server-mode source.
   *
   * `-` prefixes a descending key, which is the shape the roll-off endpoint
   * allowlists. An unsorted table sends nothing and gets newest first.
   */
  fetch = (q: DataQuery): Observable<DataPage<RollOffOpportunity>> => {
    const params = new URLSearchParams();
    params.set('page', String(q.page));
    params.set('pageSize', String(Math.min(q.pageSize, ROLLOFF_MAX_PAGE_SIZE)));
    if (q.search) params.set('search', q.search);
    if (q.sort) params.set('sort', `${q.desc ? '-' : ''}${q.sort}`);
    // The bar's rules, one repeated `rule` each, in the grammar the board
    // sends: the server holds the whitelist and refuses a column it lacks.
    for (const rule of toRuleParams(q.filters, this.filterFields)) params.append('rule', rule);

    return this.http
      .get<RollOffOpportunityPage>(`${ROLLOFF_OPPORTUNITIES_URL}?${params}`)
      .pipe(map(page => ({ items: page.items, total: page.total })));
  };

  container = rollOffContainerLabel;

  overdue(hire: RollOffOpportunity): boolean {
    return rollOffHireOverdue(hire.hireEndsOn, hire.outcomeAt);
  }

  /** The site's address on one line, the way the board's card writes it. */
  /** Shared with the Board, which prints the same line under "Show details". */
  address(hire: RollOffOpportunity): string {
    return hireAddress(hire);
  }

  /** An import or a merge changed the rows, so the table reads its page
   *  again. Which dialog opened, told which table, is the shared transfer
   *  actions' business. */
  onTransferChanged(): void {
    this.table?.reload();
    this.compactList?.reload();
  }

  /**
   * Where the whole table is written out. Handed to the shared transfer
   * actions, which draw the button, hold the busy state and report a failed
   * download - the same three the board now gets from the same component,
   * rather than a second copy of them here.
   */
  readonly exportUrl = ROLLOFF_TRANSFER_EXPORT_URL;
}
